import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ethers } from 'ethers';
import { useWeb3 } from '../contexts/Web3Context';
import { useContracts } from '../contexts/ContractContext';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { PageContainer } from '../components/ui';

const HeroSection = styled.section`
  padding: 4rem 2rem;
  text-align: center;
  max-width: ${({ theme }) => theme.layout.maxWidth};
  margin: 0 auto;
`;

const HeroTitle = styled(motion.h1)`
  font-size: 3.75rem;
  font-weight: 800;
  letter-spacing: -1px;
  background: linear-gradient(135deg, #ffffff 0%, #d8ccff 45%, #7df0ff 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  filter: drop-shadow(0 12px 40px rgba(124, 92, 255, 0.45));
  margin-bottom: 1rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const CTAButtons = styled(motion.div)`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-bottom: 4rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    align-items: center;
  }
`;

const CTAButton = styled(Link)`
  background: ${({ theme }) => theme.gradients.brand};
  color: #fff;
  padding: 1rem 2.2rem;
  border-radius: ${({ theme }) => theme.radii.pill};
  text-decoration: none;
  font-weight: 600;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: inline-block;
  box-shadow: ${({ theme }) => theme.shadows.brand};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.glow};
  }

  &.secondary {
    background: #fff;
    color: ${({ theme }) => theme.colors.primary};
    border: 2px solid ${({ theme }) => theme.colors.primary};
  }
`;

const StatsSection = styled.section`
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(18px) saturate(160%);
  -webkit-backdrop-filter: blur(18px) saturate(160%);
  border: 1px solid rgba(255, 255, 255, 0.6);
  padding: 3rem 2rem;
  margin: 2rem auto;
  max-width: ${({ theme }) => theme.layout.maxWidth};
  border-radius: ${({ theme }) => theme.radii.xl};
  box-shadow: ${({ theme }) => theme.shadows.lg};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
`;

const StatCard = styled(motion.div)`
  text-align: center;
  padding: 2rem;
  border-radius: ${({ theme }) => theme.radii.md};
  background: linear-gradient(135deg, #f8fafc, #e2e8f0);
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  color: ${({ theme }) => theme.colors.muted};
  font-weight: 500;
`;

const FeaturesSection = styled.section`
  padding: 4rem 2rem;
  max-width: ${({ theme }) => theme.layout.maxWidth};
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 3rem;
  color: #fff;
  filter: drop-shadow(0 8px 24px rgba(124, 92, 255, 0.35));
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`;

const FeatureCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(18px) saturate(160%);
  -webkit-backdrop-filter: blur(18px) saturate(160%);
  border: 1px solid rgba(255, 255, 255, 0.6);
  padding: 2rem;
  border-radius: ${({ theme }) => theme.radii.xl};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  text-align: center;
  transition: transform 0.35s ease, box-shadow 0.35s ease;

  &:hover {
    transform: translateY(-6px);
    box-shadow: ${({ theme }) => theme.shadows.glow};
  }
`;

const FeatureIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.text};
`;

const FeatureDescription = styled.p`
  color: ${({ theme }) => theme.colors.muted};
  line-height: 1.6;
`;

const Home = () => {
  const { isConnected } = useWeb3();
  const { contracts } = useContracts();
  const [stats, setStats] = useState({
    totalSupply: '0',
    totalStaked: '0',
    deployedTokens: '0',
    activeArtists: '0',
  });

  useEffect(() => {
    const loadStats = async () => {
      try {
        // Check if contracts are loaded
        if (!contracts.vesikaCoin || !contracts.factory) {
          return;
        }

        
        // Get total supply with error handling
        let totalSupply = '0';
        try {
          const supply = await contracts.vesikaCoin.totalSupply();
          totalSupply = (parseInt(supply.toString()) / 1e18 / 1000000).toFixed(1) + 'M';
        } catch (error) {
          console.error('Error getting total supply:', error);
          totalSupply = 'N/A';
        }
        
        // Get total staked with error handling
        let totalStaked = '0';
        try {
          const staked = await contracts.vesikaCoin.totalStaked();
          
          // Check if staked is valid and not empty
          if (staked && staked.toString() !== '0x' && staked.toString() !== '') {
            const stakedValue = parseFloat(ethers.utils.formatEther(staked));
            
            if (stakedValue === 0) {
              totalStaked = '0';
            } else if (stakedValue >= 1000000) {
              totalStaked = (stakedValue / 1000000).toFixed(1) + 'M';
            } else if (stakedValue >= 1000) {
              totalStaked = (stakedValue / 1000).toFixed(1) + 'K';
            } else {
              totalStaked = stakedValue.toFixed(1);
            }
          } else {
            totalStaked = '0';
          }
        } catch (error) {
          console.error('❌ Error getting total staked:', error);
          totalStaked = '0';
        }
        
        // Get deployed tokens count with error handling
        let deployedTokens = '0';
        try {
          const tokens = await contracts.factory.deployedTokenCount();
          deployedTokens = tokens.toString();
        } catch (error) {
          console.error('Error getting deployed tokens count:', error);
          deployedTokens = 'N/A';
        }
        
        // Get active artists count with error handling
        let activeArtists = '0';
        try {
          const approvedCount = await contracts.factory.getApprovedArtistCount();
          activeArtists = approvedCount.toString();
        } catch (error) {
          console.error('Error getting active artists count:', error);
          activeArtists = 'N/A';
        }

        
        setStats({
          totalSupply,
          totalStaked,
          deployedTokens,
          activeArtists,
        });
      } catch (error) {
        console.error('Error in loadStats:', error);
      }
    };

    loadStats();
    
    // Set up polling to refresh stats every 30 seconds
    const intervalId = setInterval(loadStats, 30000);
    
    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, [contracts]);

  const features = [
    {
      icon: '🎨',
      title: 'Artist Tokens',
      description: 'Artists can create their own ERC20 tokens and interact directly with their fans.',
    },
    {
      icon: '🏛️',
      title: 'DAO Governance',
      description: 'VesikaCoin holders can vote on platform decisions and shape its future direction.',
    },
    {
      icon: '💎',
      title: 'Staking Rewards',
      description: 'Stake your tokens to earn 5-15% annual yield and increase your voting power.',
    },
    {
      icon: '🔄',
      title: 'Token Swap',
      description: 'Swap securely and at low cost between the main token and artist tokens.',
    },
    {
      icon: '🛡️',
      title: 'Secure System',
      description: 'Smart contracts built and audited using OpenZeppelin standards.',
    },
    {
      icon: '🌐',
      title: 'Decentralized',
      description: 'Fully decentralized and transparent transactions powered by blockchain technology.',
    },
  ];

  return (
    <PageContainer $noPadding>
      <HeroSection>
        <HeroTitle
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          VesikaArt Ecosystem
        </HeroTitle>
        
        <HeroSubtitle
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          A decentralized token ecosystem for artists and fans.
          Create your own token, stake it, and join the DAO.
        </HeroSubtitle>

        <CTAButtons
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          {isConnected ? (
            <>
              <CTAButton to="/staking">Start Staking</CTAButton>
              <CTAButton to="/artist" className="secondary">Become an Artist</CTAButton>
            </>
          ) : (
            <>
              <CTAButton to="/staking">Connect Wallet</CTAButton>
              <CTAButton to="/artist" className="secondary">Learn More</CTAButton>
            </>
          )}
        </CTAButtons>

        <StatsSection>
          <StatsGrid>
            <StatCard
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              <StatNumber>{stats.totalSupply}</StatNumber>
              <StatLabel>Total VSK Supply</StatLabel>
            </StatCard>
            
            <StatCard
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.7 }}
            >
              <StatNumber>{stats.totalStaked}</StatNumber>
              <StatLabel>Staked VSK</StatLabel>
            </StatCard>
            
            <StatCard
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.8 }}
            >
              <StatNumber>{stats.deployedTokens}</StatNumber>
              <StatLabel>Artist Tokens</StatLabel>
            </StatCard>
            
            <StatCard
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.9 }}
            >
              <StatNumber>{stats.activeArtists}</StatNumber>
              <StatLabel>Active Artists</StatLabel>
            </StatCard>
          </StatsGrid>
        </StatsSection>
      </HeroSection>

      <FeaturesSection>
        <SectionTitle>Platform Features</SectionTitle>
        <FeaturesGrid>
          {features.map((feature, index) => (
            <FeatureCard
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1 + index * 0.1 }}
            >
              <FeatureIcon>{feature.icon}</FeatureIcon>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </FeatureCard>
          ))}
        </FeaturesGrid>
      </FeaturesSection>
    </PageContainer>
  );
};

export default Home;
